// Circuit breaker for the optimistic-concurrency conflict on
// save_briefing_core (SQLSTATE PT409, "briefing was modified by
// someone else"; recognised by isBriefingSaveConflict below).
//
// Background — the 2026-08-05 production CPU incident
// ---------------------------------------------------
// Production Postgres logged ~23 million errors in six hours — 99% of
// all database logging — every one of them this same conflict. At peak
// that was ~4,000 failed saves per second; it was still running a day
// later. The CPU alert was entirely this: Postgres does not just raise
// the exception, it writes the full statement — including the briefing
// JSONB payload — to the log on every single ERROR.
//
// A conflict is normal, expected behaviour: two people editing one
// production, or one producer with two tabs open. What was NOT normal
// is that nothing stopped the retrying.
//
// Root cause (found 2026-10): PostgREST retried the old SQLSTATE 40001
// itself; migration 20261021100000 raises PT409 instead.
//
// This breaker is the second line of defence: it bounds a client that
// keeps re-sending a save that conflicts. It lives in front of the one
// function every save funnels through, so it bounds the damage no
// matter which caller is doing the hammering. Keeping it as pure logic
// (no React, no Supabase) is what makes it testable in a unit test.
//
// Why a blanket minimum interval between saves was rejected
// ---------------------------------------------------------
// It would silently drop a legitimate save that follows another within
// the window — the first-publish path saves a draft and then publishes,
// with no guaranteed gap. Losing a producer's work is worse than the
// load it would prevent. It is also unnecessary: a save that SUCCEEDS
// does real work and waits on a network round trip, so even a runaway
// caller tops out at a handful per second. The conflict path is the
// pathological one precisely because it fails instantly, before doing
// any work.

/**
 * SQLSTATE save_briefing_core raises on a stale p_expected_updated_at
 * (migration 20261021100000). PostgREST answers it once with HTTP 409
 * and passes the code through as `error.code`.
 */
const BRIEFING_SAVE_CONFLICT_CODE = "PT409";

/**
 * Is this error from save_briefing_core the optimistic-concurrency
 * conflict? Matches on the SQLSTATE only, never on HTTP 409: PostgREST
 * also answers a unique violation (23505) with 409.
 */
export function isBriefingSaveConflict(error: unknown): boolean {
  if (typeof error !== "object" || error === null || !("code" in error)) return false;
  return error.code === BRIEFING_SAVE_CONFLICT_CODE;
}

/** Backoff after the Nth consecutive conflict, in milliseconds. */
export const CONFLICT_BACKOFF_MS = [1_000, 4_000, 15_000] as const;

/**
 * Consecutive conflicts after which the breaker stops trying. At that
 * point the version token is not converging and every further attempt
 * is pure load.
 */
export const CONFLICT_TRIP_THRESHOLD = CONFLICT_BACKOFF_MS.length + 1;

/**
 * How long a tripped breaker stays shut before it lets one save
 * through again.
 *
 * It deliberately recovers rather than latching until reload. A
 * permanent latch is the wrong trade: it would leave a producer who hit
 * four genuine conflicts unable to save their own work until they think
 * to reload, and the breaker lives outside the component, so navigating
 * away and back does not clear it.
 *
 * Recovering costs almost nothing: a caller that never converges gets
 * CONFLICT_TRIP_THRESHOLD attempts a minute instead of an unbounded
 * loop, and it cannot lock anybody out of their evening.
 */
export const TRIP_RECOVERY_MS = 60_000;

export type SaveAttemptDecision =
  /** Go ahead. */
  | { allowed: true }
  /** Too soon — `retryAfterMs` says when the caller may try again. */
  | { allowed: false; reason: "cooldown"; retryAfterMs: number }
  /** Give up. Only a reset clears this. */
  | { allowed: false; reason: "tripped" };

export interface SaveConflictBreaker {
  /**
   * Ask permission before firing a save. Also the point at which a
   * tripped breaker notices its recovery window has passed, so this is
   * not side-effect free.
   */
  attempt(): SaveAttemptDecision;
  /** Record a save conflict (isBriefingSaveConflict); arms the next backoff window. */
  recordConflict(): void;
  /** Record a save that reached the database; clears the streak. */
  recordSuccess(): void;
  /** Consecutive conflicts so far — reported when the breaker trips. */
  conflictStreak(): number;
  /** Clear all state, including a trip. */
  reset(): void;
}

/**
 * @param now Injectable clock so tests do not have to sleep.
 */
export function createSaveConflictBreaker(now: () => number = Date.now): SaveConflictBreaker {
  let streak = 0;
  let blockedUntil = 0;
  let tripped = false;

  return {
    attempt() {
      const t = now();

      if (tripped) {
        if (t < blockedUntil) return { allowed: false, reason: "tripped" };
        // The recovery window has passed — start over and let one
        // through. If the caller is still looping it trips again after
        // CONFLICT_TRIP_THRESHOLD attempts.
        tripped = false;
        streak = 0;
        blockedUntil = 0;
        return { allowed: true };
      }

      if (t < blockedUntil) {
        return { allowed: false, reason: "cooldown", retryAfterMs: blockedUntil - t };
      }
      return { allowed: true };
    },

    recordConflict() {
      streak += 1;
      if (streak >= CONFLICT_TRIP_THRESHOLD) {
        tripped = true;
        blockedUntil = now() + TRIP_RECOVERY_MS;
        return;
      }
      blockedUntil = now() + CONFLICT_BACKOFF_MS[streak - 1];
    },

    recordSuccess() {
      streak = 0;
      blockedUntil = 0;
    },

    conflictStreak() {
      return streak;
    },

    reset() {
      streak = 0;
      blockedUntil = 0;
      tripped = false;
    },
  };
}

/**
 * Thrown when the brake refuses a save.
 *
 * Deliberately an error and not a `null` return. `updateProduction`
 * already returns `null` for "nothing to do" (no production yet, save
 * already in flight), and its caller treats that as harmless — it
 * carries on writing the related tables and shows a success toast. A
 * refused save is the opposite: the briefing fields were NOT written.
 * Returning `null` produced a half-save presented as a success, right
 * after telling the producer saving was paused. Throwing makes the
 * whole save abort coherently, with one error toast.
 */
export class SaveBlockedError extends Error {
  readonly reason: "cooldown" | "tripped";

  constructor(reason: "cooldown" | "tripped", message: string) {
    super(message);
    this.name = "SaveBlockedError";
    this.reason = reason;
  }
}

/** The message a producer sees when a save is refused. */
export function saveBlockedMessage(decision: Exclude<SaveAttemptDecision, { allowed: true }>) {
  if (decision.reason === "tripped") {
    return "This production keeps being changed elsewhere, so saving is paused for a minute. Reload the page to pick up the latest version now.";
  }
  const seconds = Math.max(1, Math.ceil(decision.retryAfterMs / 1000));
  return `The last save hit a conflict. Wait ${seconds} second${seconds === 1 ? "" : "s"} and try again.`;
}

// The breakers, keyed by production.
//
// Module-level rather than a ref inside the hook, and this is the whole
// point: a single mounted hook cannot loop fast — updateProduction
// serialises to one in-flight call, and the conflict path costs two
// round trips. A browser that hammers needs many tabs or a REMOUNT
// loop, and a breaker stored per mount would be handed back fresh,
// streak zeroed, on every remount. Keeping it here is what makes "stops
// trying" actually true.
const breakers = new Map<string, SaveConflictBreaker>();

export function breakerForProduction(productionId: string): SaveConflictBreaker {
  let breaker = breakers.get(productionId);
  if (!breaker) {
    breaker = createSaveConflictBreaker();
    breakers.set(productionId, breaker);
  }
  return breaker;
}

/** Forget a production's breaker — the producer has moved on. */
export function resetBreakerForProduction(productionId: string | null | undefined) {
  if (productionId) breakers.delete(productionId);
}

/** Test seam: drop every breaker. */
export function __resetAllBreakers() {
  breakers.clear();
}
